import { useEffect, useState } from 'react';

import { environment } from 'environments/environment';
import { DssTemaEnum } from 'enums/dss-tema.enum';
import { isDssLayout } from '../layout/dss/dss';
import axiosInstance from '../http/axios-instance';
import {
  saveTenantSchemaStorage,
  clearTenantSchemaStorage,
  clearTenantStorage,
  saveTenantStorage,
  saveUserStorage,
  clearUserStorage,
  clearHandshakeCredentialsStorage,
  hasStoredSession,
  DSS_TOKEN_STORAGE_KEY,
  DSS_IID_STORAGE_KEY,
} from './dss-session-storage';
import { syncDssLocale } from './dss-locale';

/** Payload esperado do `postMessage` enviado pelo Sigma Cloud (iframe pai) no handshake DSS. */
export interface DssMessage {
  theme?: string;
  token?: string;
  integradorId?: string;
  tenant?: unknown;
}

export interface DssSessionState {
  /** true após receber e gravar uma mensagem válida do Sigma client. */
  loaded: boolean;
  /** tema corrente recebido do pai (dark por padrão). */
  theme: DssTemaEnum;
}

// Chaves de sessionStorage gravadas pelo handshake, espelhando o branch DSS de
// app.component.ts e lidas depois por readDssTheme() (theme) e pelo leaf Angular.
// As credenciais (token, iid) ficam em dss-session-storage.ts, junto da limpeza delas.
export const DSS_THEME_STORAGE_KEY = 'theme';
// Espelha INTEGRADOR_UUID de auth.service.ts. O handshake DSS não grava essa chave;
// ela é lida apenas para respeitar a precedência de UsuarioService.getUsuario() caso
// um fluxo anterior a tenha deixado no storage.
export const DSS_INTEGRADOR_UUID_STORAGE_KEY = 'INTEGRADOR_UUID';

// Strings do protocolo com o Sigma Cloud — não alterar sem coordenar com o time do Sigma.
const READY_MESSAGE = 'center está apto a receber mensagens do sigma';
const LOADED_MESSAGE = 'loaded';

function isPlainObject(data: unknown): data is Record<string, unknown> {
  return typeof data === 'object' && data !== null;
}

// As quatro chaves são exigidas em conjunto: uma mensagem parcial não é persistida.
function hasCompleteSessionFields(data: Record<string, unknown>): data is Required<DssMessage> {
  return (
    data.theme != null && data.token != null && data.integradorId != null && data.tenant != null
  );
}

function resolveTheme(theme: string): DssTemaEnum {
  return theme === DssTemaEnum.LIGHT ? DssTemaEnum.LIGHT : DssTemaEnum.DARK;
}

/**
 * Endpoint do usuário logado, espelhando a precedência de UsuarioService.getUsuario():
 * o uuid do integrador vence o IID quando presente. Valor vazio cai no IID, como no
 * `if (uuid)` do Angular.
 */
function buildUserEndpoint(integradorId: string): string {
  const uuid = sessionStorage.getItem(DSS_INTEGRADOR_UUID_STORAGE_KEY);
  return `api/v1/usuarios/me/integrador/${uuid && uuid.length ? uuid : integradorId}`;
}

/**
 * Porta do handshake de sessão DSS (branch `isDssLayout()` de `app.component.ts`):
 * escuta `postMessage` do Sigma Cloud, valida origem/formato e grava a sessão no
 * `sessionStorage`. Sem efeito fora do modo DSS.
 */
export function useDssSession(): DssSessionState {
  // Acesso direto à rota DSS (janela de topo) com sessão já gravada por um login normal:
  // não há Sigma para fazer o handshake, então reaproveita a sessão, como o legado, que
  // só avaliava o modo DSS no bootstrap e deixava a troca de hash usar a sessão existente.
  // Decidido uma única vez por montagem. Dentro do iframe do Sigma o handshake sempre
  // vale, porque o storage pode carregar sessão velha de uma visita anterior.
  const [reuseStoredSession] = useState(
    () => isDssLayout() && window.parent === window && hasStoredSession(),
  );
  const [state, setState] = useState<DssSessionState>(() =>
    reuseStoredSession
      ? { loaded: true, theme: resolveTheme(sessionStorage.getItem(DSS_THEME_STORAGE_KEY) ?? '') }
      : { loaded: false, theme: DssTemaEnum.DARK },
  );

  useEffect(() => {
    if (reuseStoredSession || !isDssLayout()) {
      return undefined;
    }

    const trustedOrigin = environment.urlOriginSigmaClient;

    async function handleMessage(event: MessageEvent): Promise<void> {
      if (event.origin !== trustedOrigin) {
        return;
      }
      if (!isPlainObject(event.data)) {
        return;
      }
      if (!hasCompleteSessionFields(event.data)) {
        return;
      }

      const data = event.data;
      const theme = resolveTheme(data.theme);

      sessionStorage.setItem(DSS_THEME_STORAGE_KEY, data.theme);
      // O Sigma pode entregar o token como objeto ou como string JSON — normaliza para
      // string antes de gravar, evitando que sessionStorage converta para "[object Object]".
      sessionStorage.setItem(
        DSS_TOKEN_STORAGE_KEY,
        typeof data.token === 'string' ? data.token : JSON.stringify(data.token),
      );
      sessionStorage.setItem(DSS_IID_STORAGE_KEY, data.integradorId);

      // O Sigma entrega apenas o schema do tenant (string identificadora), não o objeto
      // Tenant completo. O Angular leaf lê o tenant via getTenantStorage(), que espera
      // Base64(JSON(Tenant)). Gravando o schema bruto diretamente causaria falha no atob().
      //
      // Solução: gravar o schema como tenant_schema temporário (o axios-instance React
      // já usa essa chave para montar o header x-tenant), buscar o objeto completo em
      // admin/tenants/dss_layout e então persistir no formato Base64 esperado pelo Angular.
      // Esse é o mesmo fluxo que app.component.ts executa no modo Angular clássico.
      const tenantSchema =
        typeof data.tenant === 'string' ? data.tenant : JSON.stringify(data.tenant);
      // Espelha limparTenantStorage() de app.component.ts, que roda antes de
      // salvarTenantSchemaStorage() no handshake Angular. Sem isso, o `tenant` que
      // sobreviveu no sessionStorage da visita anterior coexiste com o
      // `tenant_schema` recém-gravado e o XOR de getTenantHeader() anula o header.
      clearTenantStorage();
      saveTenantSchemaStorage(tenantSchema);

      try {
        const response = await axiosInstance.get<unknown>('admin/tenants/dss_layout');
        clearTenantSchemaStorage();
        saveTenantStorage(response.data);
      } catch {
        // Falha na busca do tenant: limpa o schema temporário para não deixar estado
        // inconsistente e aborta — 'loaded' não é enviado, o Sigma não avança.
        // Combinado com o clearTenantStorage() acima, as duas chaves ficam ausentes,
        // então a próxima montagem parte de estado limpo em vez de reincidir no XOR
        // anulado. Não remover nenhuma das duas limpezas.
        clearTenantSchemaStorage();
        clearHandshakeCredentialsStorage();
        return;
      }

      // O usuário logado precisa estar no storage antes da folha Angular montar: telas
      // de feature leem authService.getUsuarioStorage() no ngOnInit e acessam
      // propriedades do retorno sem checar null (ex.: add-whatsapp). Sem esta etapa —
      // que o branch DSS de app.component.ts executava antes de liberar o render — o
      // ngOnInit lança, o change detection para no ponto da falha e o resto do template
      // fica sem atualizar. Depende do tenant já persistido, porque é ele que compõe o
      // header `tenant` desta requisição.
      try {
        const response = await axiosInstance.get<unknown>(buildUserEndpoint(data.integradorId));
        saveUserStorage(response.data);
      } catch {
        // Sem usuário não há sessão utilizável: aborta sem 'loaded' e limpa o que foi
        // gravado, para a próxima montagem não herdar meia sessão (tenant sem usuário,
        // ou credencial sem nenhum dos dois).
        clearUserStorage();
        clearTenantStorage();
        clearHandshakeCredentialsStorage();
        return;
      }

      // Antes de liberar o conteúdo: a folha Angular lê o locale uma única vez, no
      // bootstrap. Sem esta etapa, que o AppComponent fazia no caminho legado, a tela
      // ignora o idioma do usuário. Não aborta o handshake em falha.
      await syncDssLocale();

      setState({ loaded: true, theme });
      // Fora de um iframe real (ex: acesso direto à rota em dev/teste), window.parent === window
      // e a origem não confere com trustedOrigin, fazendo o postMessage lançar. A sessão já foi
      // gravada acima; a confirmação ao pai é best-effort e não deve propagar erro ao chamador.
      try {
        window.parent.postMessage(LOADED_MESSAGE, trustedOrigin);
      } catch {
        // Sem pai confiável para notificar — nada a fazer.
      }
    }

    window.addEventListener('message', handleMessage);
    // Mesma situação do postMessage de "loaded": sem isso, um lançamento aqui impediria o
    // useEffect de alcançar o cleanup abaixo, vazando o listener registrado logo acima.
    try {
      window.parent.postMessage(READY_MESSAGE, trustedOrigin);
    } catch {
      // Sem pai confiável para notificar — o listener continua registrado normalmente.
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [reuseStoredSession]);

  return state;
}
